import { ImageBackground, StyleSheet, View } from 'react-native';
import QRCode from 'react-native-qrcode-svg';
import Svg, { Defs, Mask, Path, Rect } from 'react-native-svg';
import { Icon, useTheme } from '@ui-kitten/components';
import dayjs from 'dayjs';
import lodashFind from 'lodash/find';

import Button from '../../Components/Button';
import ResponsiveText from '../../Components/ResponsiveText';
import constants from '../../Config/constants';
import { numberToArray, pluralize } from '../../Helper';
import { OrderDetails, OrderItem } from '../../RTK/api/orders/types';
import themeColor from '../../Theme/colors';
import themeStyle from '../../Theme/styles';

type TicketProps = OrderDetails & {
  contentContainerStyle?: any;
  index: number;
  readOnly?: boolean;
  withShadow?: boolean;
  onGoToLocation: (data: OrderDetails) => void;
  onShare: (data: OrderDetails, qrValue: string) => void;
};
type LabelAndDetailsProps = {
  label: string;
  value: string;
  noMargin?: boolean;
};
export const dashCount = 7;
const radius = 15;

const LabelAndDetails = ({ label, value, noMargin }: LabelAndDetailsProps) => (
  <View style={[themeStyle.flex1, !noMargin && themeStyle.spacingBottomMedium]}>
    <ResponsiveText status="primary" style={themeStyle.bold}>
      {label}
    </ResponsiveText>
    <ResponsiveText style={themeStyle.bold}>{value}</ResponsiveText>
  </View>
);

export const TicketCurved = ({
  isRight,
  withBorder,
}: {
  isRight?: boolean;
  withBorder?: boolean;
}) => (
  <View style={isRight && { transform: [{ rotate: '180deg' }] }}>
    <Svg width="36" height="36" viewBox="0 0 36 36">
      <Defs>
        <Mask id="mask" x="0" y="0" width="36" height="36">
          {/* White = visible, black = hidden */}
          <Rect width="36" height="36" fill="white" />
          <Path d="M 0 0 a 1 1 0 0 1 0 36" fill="black" strokeWidth={1} />
        </Mask>
      </Defs>
      {/* This rect will have a curve punched out of it */}
      <Rect width="36" height="36" fill="#fff" mask="url(#mask)" />
      {/* Manual fake shadow: offset slightly, low opacity */}
      {withBorder && (
        <Path
          d="M 0 0 a 1 1 0 0 1 0 36"
          fill="none"
          stroke={themeColor.black}
          strokeWidth={4}
          opacity={0.2} // 👈 adjust as needed
          transform="translate(-1, 0)" // 👈 shift to the left for outside shadow
        />
      )}
      {/* Border on the curved part */}
      {withBorder && (
        <Path
          d="M 0 0 a 1 1 0 0 1 0 36"
          fill="none"
          stroke={themeColor.black} // 👈 customise border color here
          strokeWidth={1}
        />
      )}
    </Svg>
  </View>
);

const Ticket = (props: TicketProps) => {
  const theme = useTheme();
  const {
    contentContainerStyle,
    index,
    readOnly,
    withShadow,
    onGoToLocation,
    onShare,
    ...data
  } = props;
  const {
    customer,
    event_details,
    id,
    items,
    order_date,
    order_time,
    status,
    store_details,
  } = data;
  const qrValue = `qrg://${id}_${index + 1}`;
  const isRedeemed = status !== 'new';
  const itemWithImage = lodashFind(items, (i) => i.image) as OrderItem;
  const ticketImage = itemWithImage?.image || store_details.partner_banner; // fallback to store image
  const completeItemInfo = items?.map?.((item) => item.name)?.join?.(' - ');
  const isAsap = order_time === 'asap_order_time';
  return (
    <View style={[styles.ticketContainer, contentContainerStyle]}>
      {/* Ticket Content */}
      <View
        style={[
          styles.ticketContent,
          withShadow && { ...styles.shadow, ...styles.shadowTop },
        ]}
      >
        <ImageBackground
          source={{ uri: ticketImage }}
          style={styles.imageBackground} // from the design
        />
        <View
          style={[
            themeStyle.pageVerticalSpacing,
            themeStyle.pageHorizontalSpacing,
          ]}
        >
          <View style={themeStyle.spacingBottomMedium}>
            <View style={[themeStyle.flex1, themeStyle.flexDirectionRowCenter]}>
              <ResponsiveText
                category="h3"
                style={[themeStyle.flex1, themeStyle.bold]}
              >
                {store_details?.name}
              </ResponsiveText>
              {!readOnly && (
                <Button
                  hitSlop={10}
                  onPress={() => onShare(data, qrValue)}
                  plain
                >
                  <Icon
                    name="share-outline"
                    fill={theme['color-info-500']}
                    style={styles.icon}
                  />
                </Button>
              )}
            </View>
            <ResponsiveText category="s1">{completeItemInfo}</ResponsiveText>
          </View>
          <LabelAndDetails
            label="Reservation Name"
            value={`${customer.first_name} ${customer.last_name}`}
          />
          <View style={themeStyle.flexDirectionRow}>
            <LabelAndDetails
              label="Location"
              value={event_details?.location_address || store_details.location}
            />
            {!readOnly && (
              <Button hitSlop={10} onPress={() => onGoToLocation(data)} plain>
                <Icon
                  name="navigation-2-outline"
                  fill={theme['color-info-500']}
                  style={styles.icon}
                />
              </Button>
            )}
          </View>
          <LabelAndDetails
            label="Date"
            value={dayjs(order_date).format(constants.DATE_DISPLAY_FORMAT2)}
          />
          <View style={themeStyle.flexDirectionRow}>
            <LabelAndDetails
              label="Time"
              value={
                isAsap
                  ? dayjs(event_details?.start_date).format(
                      constants.TIME_ONLY_DISPLAY_CAPITAL_FORMAT,
                    )
                  : dayjs(order_time, constants.DBTIME_FORMAT).format(
                      constants.TIME_ONLY_DISPLAY_CAPITAL_FORMAT,
                    )
              }
              noMargin
            />
            <View style={themeStyle.rowSeparator} />
            <LabelAndDetails
              label="Number of guests"
              value={`${items[0]?.quantity} ${pluralize(
                items[0]?.quantity,
                'guest',
              )}`}
              noMargin
            />
          </View>
        </View>
      </View>
      {/* Curved and dashed line */}
      <View style={styles.ticketCutOffPart}>
        <TicketCurved withBorder={withShadow} />
        <View style={styles.ticketDashContainer}>
          {numberToArray(dashCount).map((_, i) => (
            <View
              key={i}
              style={[
                themeStyle.flex1,
                themeStyle.pageHorizontalSpacingSmall,
                themeStyle.flexDirectionRowCenterCenter,
              ]}
            >
              <View
                style={[
                  styles.ticketDashed,
                  { backgroundColor: theme['text-basic-color'] },
                ]}
              />
            </View>
          ))}
        </View>
        <TicketCurved withBorder={withShadow} isRight />
      </View>
      {/* QR Code Container */}
      <View
        style={[
          styles.ticketQr,
          withShadow && { ...styles.shadow, ...styles.shadowBottom },
        ]}
      >
        <View>
          {isRedeemed && (
            <View style={styles.redeemedContainer}>
              <ResponsiveText
                category="h1"
                style={[themeStyle.bold, styles.redeemedText]}
              >
                REDEEMED
              </ResponsiveText>
            </View>
          )}
          <QRCode
            color={isRedeemed ? '#0000009C' : undefined}
            size={200}
            value={qrValue}
          />
        </View>
        <ResponsiveText category="c2" style={themeStyle.spacingTopMedium}>
          *Present this bar code at the restaurant*
        </ResponsiveText>
      </View>
    </View>
  );
};

const REDEEMED_POS = -25;
export const styles = StyleSheet.create({
  ticketContainer: {
    alignSelf: 'center',
    borderRadius: radius,
    maxWidth: 350,
    width: '100%',
  },
  ticketContent: {
    ...themeStyle.pageBackground,
    borderTopLeftRadius: radius,
    borderTopRightRadius: radius,
    overflow: 'hidden',
  },
  imageBackground: {
    height: 218,
  },
  icon: { width: 25, height: 25 },
  ticketCutOffPart: {
    ...themeStyle.flexDirectionRow,
    height: 36,
  },
  ticketCutOut: {
    height: '100%',
    aspectRatio: 1,
  },
  ticketDashContainer: {
    ...themeStyle.flex1,
    ...themeStyle.flexDirectionRowCenterCenter,
    ...themeStyle.pageBackground,
  },
  ticketDashed: {
    height: 1,
    width: '100%',
    marginHorizontal: 10,
  },
  shadow: {
    ...themeStyle.shadowOverAll,
    borderWidth: 1,
    borderColor: themeColor.black,
  },
  shadowTop: {
    borderBottomWidth: 0,
  },
  shadowBottom: {
    shadowOffset: { width: 0, height: 4 }, // left, bottom and right shadow only
    borderTopWidth: 0,
  },
  ticketQr: {
    ...themeStyle.pageBackground,
    ...themeStyle.alignItemsCenter,
    ...themeStyle.pageVerticalSpacingSmall,
    ...themeStyle.pageHorizontalSpacing,
    borderBottomLeftRadius: radius,
    borderBottomRightRadius: radius,
  },
  redeemedContainer: {
    ...themeStyle.flexDirectionRowCenterCenter,
    ...StyleSheet.absoluteFillObject,
    position: 'absolute',
    top: REDEEMED_POS,
    bottom: REDEEMED_POS,
    left: REDEEMED_POS,
    right: REDEEMED_POS,
    zIndex: 1,
  },
  redeemedText: {
    textShadowColor: themeColor.gray,
    textShadowRadius: 5,
    textShadowOffset: { width: 3, height: 3 },
    transform: [{ rotate: '-45deg' }],
  },
});

export default Ticket;
